import type { ReduxSuppliers } from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import {
	aicpCategories,
	aicpSubCategories,
	categories,
	cnc5chCategories,
	cnc5chSubCategories,
	photoGridCategories,
	photoGridSubCategories,
	postProductionCategories,
	quote360TemplateCategories,
	quoteAnimationAndLiveActionCategories,
	quoteAnimationAndLiveActionSubCategories,
	quoteAnimationOnlyCategories,
	quoteAnimationOnlySubCategories,
	quoteDigitalCategories,
	quoteDigitalSubCategories,
	soundProdCategories,
	soundProdSubCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import { quoteTypes } from 'src/utils/typeUtils'

type UpdatedSupplierData = ReduxSuppliers & {
	apfpCategoryUuid: string
	postProdCategoryUuid: string
	aicpSubCategoryUuid: string
	photoSubCategoryUuid: string
	soundProdSubCategoryUuid: string
	cnc5chSubCategoryUuid: string
	quoteAnimationOnlySubCategoryUuid: string
	quoteAnimationAndLiveActionSubCategoryUuid: string
	quoteDigitalSubCategoryUuid: string
	quote360TemplateCategoryUuid: string
}

function getSupplierUuids({
	newSupplierData,
}: {
	newSupplierData: UpdatedSupplierData
}) {
	const {
		apfpCategoryUuid,
		postProdCategoryUuid,
		aicpSubCategoryUuid,
		photoSubCategoryUuid,
		soundProdSubCategoryUuid,
		cnc5chSubCategoryUuid,
		quoteAnimationOnlySubCategoryUuid,
		quoteAnimationAndLiveActionSubCategoryUuid,
		quoteDigitalSubCategoryUuid,
		quote360TemplateCategoryUuid,
		...updatedSupplierData
	} = newSupplierData

	function getCategoryData({
		quoteType,
		supplierCategoryUuid,
	}: {
		quoteType: QuoteType
		supplierCategoryUuid?: string
	}) {
		switch (quoteType) {
			case 'frenchApfp':
				return categories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'frenchPostProd':
				return postProductionCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'aicp':
				return aicpCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'photoGrid':
				return photoGridCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'soundProd':
				return soundProdCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'cnc5ch':
				return cnc5chCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'quoteAnimationOnly':
				return quoteAnimationOnlyCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'quoteAnimationAndLiveAction':
				return quoteAnimationAndLiveActionCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case 'blankQuote':
				return
			case 'quoteDigital':
				return quoteDigitalCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			case '360Template':
				return quote360TemplateCategories.find(
					(c) => c.categoryUuid === supplierCategoryUuid
				)
			default: {
				const tsCheck: never = quoteType
				break
			}
		}
	}

	function getSubCategoryData({
		quoteType,
		supplierSubCategoryUuid,
	}: {
		quoteType: QuoteType
		supplierSubCategoryUuid?: string
	}) {
		switch (quoteType) {
			case 'frenchApfp':
				return
			case 'frenchPostProd':
				return
			case 'aicp':
				return aicpSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'photoGrid':
				return photoGridSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'soundProd':
				return soundProdSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'cnc5ch':
				return cnc5chSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'quoteAnimationOnly':
				return quoteAnimationOnlySubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'quoteAnimationAndLiveAction':
				return quoteAnimationAndLiveActionSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case 'blankQuote':
				return
			case 'quoteDigital':
				return quoteDigitalSubCategories.find(
					(c) => c.subCategoryUuid === supplierSubCategoryUuid
				)
			case '360Template':
				return
			default: {
				const tsCheck: never = quoteType
				break
			}
		}
	}

	function getSupplierUuid(quoteType: QuoteType) {
		switch (quoteType) {
			case 'frenchApfp':
				return apfpCategoryUuid
			case 'frenchPostProd':
				return postProdCategoryUuid
			case 'aicp':
				return aicpSubCategoryUuid
			case 'photoGrid':
				return photoSubCategoryUuid
			case 'soundProd':
				return soundProdSubCategoryUuid
			case 'cnc5ch':
				return cnc5chSubCategoryUuid
			case 'quoteAnimationOnly':
				return quoteAnimationOnlySubCategoryUuid
			case 'quoteAnimationAndLiveAction':
				return quoteAnimationAndLiveActionSubCategoryUuid
			case 'blankQuote':
				return
			case 'quoteDigital':
				return quoteDigitalSubCategoryUuid
			case '360Template':
				return quote360TemplateCategoryUuid
			default: {
				const tsCheck: never = quoteType
				break
			}
		}
	}

	const supplierGridSectionDetails = quoteTypes.map((qt) => {
		const supplierUuid = getSupplierUuid(qt)

		if (
			['frenchApfp', 'frenchPostProd', '360Template'].some(
				(c) => c === qt
			)
		) {
			const categoryData = getCategoryData({
				quoteType: qt,
				supplierCategoryUuid: supplierUuid,
			})

			return {
				quoteType: qt,
				categoryUuid: supplierUuid,
				subCategoryUuid: `${supplierUuid}-subInvoice`,
				categoryName: categoryData?.category,
				subCategoryName: categoryData?.category,
				categoryName_fr: categoryData?.category_fr,
				subCategoryName_fr: categoryData?.category_fr,
			}
		}
		const subCategoryData = getSubCategoryData({
			quoteType: qt,
			supplierSubCategoryUuid: supplierUuid,
		})
		const categoryData = getCategoryData({
			quoteType: qt,
			supplierCategoryUuid: subCategoryData?.categoryUuid,
		})
		return {
			quoteType: qt,
			categoryUuid: categoryData?.categoryUuid,
			subCategoryUuid: supplierUuid,
			categoryName: categoryData?.category,
			subCategoryName: subCategoryData?.subCategory,
			categoryName_fr: categoryData?.category_fr,
			subCategoryName_fr: subCategoryData?.subCategory_fr,
		}
	})
	console.log('supplierGridSectionDetails: ', supplierGridSectionDetails)

	updatedSupplierData.quoteSectionDetails = supplierGridSectionDetails
	return updatedSupplierData
}

function getUpdatedSupplierUuids({
	newSupplierData,
}: {
	newSupplierData: UpdatedSupplierData
}) {
	if (
		newSupplierData.apfpCategoryUuid ||
		newSupplierData.postProdCategoryUuid ||
		newSupplierData.aicpSubCategoryUuid ||
		newSupplierData.photoSubCategoryUuid ||
		newSupplierData.soundProdSubCategoryUuid ||
		newSupplierData.quoteAnimationOnlySubCategoryUuid ||
		newSupplierData.quoteAnimationAndLiveActionSubCategoryUuid ||
		newSupplierData.quoteDigitalSubCategoryUuid ||
		newSupplierData.quote360TemplateCategoryUuid
	) {
		return getSupplierUuids({ newSupplierData })
	}
	return newSupplierData
}

export { getSupplierUuids, getUpdatedSupplierUuids }
